import { useState, useEffect } from "react";
import {
  Menu,
  X,
  ChevronUp,
  ChevronDown,
  Users,
  Package,
  BookOpen,
  Ticket,
  HelpCircle,
  Upload,
  Brain,
  LogOut,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [mlOpen, setMlOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const isUploadsActive = location.pathname.startsWith("/uploads");
  const isMlActive = location.pathname.startsWith("/ml");

  const [adminData, setAdminData] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem("adminUserData");
    if (stored) {
      setAdminData(JSON.parse(stored));
    }
  }, []);

  const name =
    adminData.name || adminData.email?.split("@")[0] || "Super Admin";
  const email = adminData.email || "superadmin@gmail.com";

  const menuItems = [
    { name: "Users", path: "/all-users", icon: Users },
    { name: "Products", path: "/category", icon: Package },
    { name: "Blog", path: "/blog", icon: BookOpen },
    { name: "Coupons", path: "/coupons", icon: Ticket },
    { name: "Queries", path: "/queries", icon: HelpCircle },
  ];

  return (
    <>
      {/* Mobile Hamburger */}
      <button
        onClick={() => setOpen(true)}
        className="fixed top-4 left-4 z-50 md:hidden p-2 rounded-lg bg-white shadow"
      >
        <Menu size={22} />
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
        />
      )}

      <aside
  className={`fixed md:static top-0 left-0 h-screen w-64 bg-gray-50 border-r z-50
transform transition-transform duration-300 
${open ? "translate-x-0 pointer-events-auto" : "-translate-x-full pointer-events-none"}
md:translate-x-0 md:pointer-events-auto`}
>
        <div className="flex flex-col h-full justify-between overflow-visible">
          {/* TOP */}
          <div>
            {/* Logo */}
            <div className="p-4 flex justify-between items-center">
              <img
                src="http://res.cloudinary.com/dt8emxboh/image/upload/v1766945624/lfuftjjhgmcebxsn0jsz.png"
                className="h-10"
              />
              <button onClick={() => setOpen(false)} className="md:hidden mr-1">
                <X />
              </button>
            </div>

            {/* MENU */}
            <nav className="px-3 space-y-1">
              {/* Normal Items */}
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <div
                    key={item.name}
                    onClick={() => {
                      navigate(item.path);
                      setOpen(false);
                    }}
                    className={`group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition
                      ${
                        isActive
                          ? "bg-green-100 text-green-700 font-semibold"
                          : "hover:bg-gray-100"
                      }`}
                  >
                    <Icon
                      size={18}
                      className={`transition ${
                        isActive
                          ? "text-green-700"
                          : "text-gray-500 group-hover:text-green-700"
                      }`}
                    />
                    <span
                      className={`transition ${
                        isActive
                          ? "text-green-700"
                          : "group-hover:text-green-700"
                      }`}
                    >
                      {item.name}
                    </span>
                  </div>
                );
              })}

              {/* UPLOADS */}
              <div className="relative">
                <div
                  onClick={() => {
                    setUploadOpen(!uploadOpen);
                    setMlOpen(false);
                  }}
                  className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition
      ${
        isUploadsActive
          ? "bg-green-100 text-green-700 font-semibold"
          : "hover:bg-gray-100"
      }`}
                >
                  <div className="flex items-center gap-3">
                    <Upload
                      size={18}
                      className={
                        isUploadsActive
                          ? "text-green-700"
                          : "text-gray-500 group-hover:text-green-700"
                      }
                    />
                    <span>Uploads</span>
                  </div>

                  <ChevronRight
                    size={16}
                    className={
                      isUploadsActive
                        ? "text-green-700"
                        : "text-gray-400 group-hover:text-green-700"
                    }
                  />
                </div>

                {uploadOpen && (
                  <div className="absolute left-full top-0 ml-4 bg-white border rounded-md shadow w-52 z-50">
                    {[
                      { name: "Carousel Manager", path: "/uploads/carousel" },
                      { name: "Collaborators", path: "/uploads/collaborators" },
                      { name: "Solutions", path: "/uploads/behind-idea" },
                      { name: "Testimonials", path: "/uploads/testimonials" },
                    ].map((item) => (
                      <div
                        key={item.name}
                        onClick={() => navigate(item.path)}
                        className="px-4 py-2 text-left hover:bg-green-50 hover:text-green-700 cursor-pointer transition"
                      >
                        {item.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ✅ QUOTES (NEW – BETWEEN UPLOADS & ML RESULT) */}
              <div
                onClick={() => navigate("/quotes")}
                className={`group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition
                  ${
                    location.pathname === "/quotes"
                      ? "bg-green-100 text-green-700 font-semibold"
                      : "hover:bg-gray-100"
                  }`}
              >
                <MessageSquare
                  size={18}
                  className={`transition ${
                    location.pathname === "/quotes"
                      ? "text-green-700"
                      : "text-gray-500 group-hover:text-green-700"
                  }`}
                />
                <span className="group-hover:text-green-700">Quotes</span>
              </div>

              {/* ML RESULT */}
              <div className="relative">
                <div
                  onClick={() => {
                    setMlOpen(!mlOpen);
                    setUploadOpen(false);
                  }}
                  className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition
      ${
        isMlActive
          ? "bg-green-100 text-green-700 font-semibold"
          : "hover:bg-gray-100"
      }`}
                >
                  <div className="flex items-center gap-3">
                    <Brain
                      size={18}
                      className={
                        isMlActive
                          ? "text-green-700"
                          : "text-gray-500 group-hover:text-green-700"
                      }
                    />
                    <span>ML Result</span>
                  </div>

                  <ChevronRight
                    size={16}
                    className={
                      isMlActive
                        ? "text-green-700"
                        : "text-gray-400 group-hover:text-green-700"
                    }
                  />
                </div>

                {mlOpen && (
                  <div className="absolute left-full top-0 ml-5 bg-white border rounded-md shadow w-52 z-50">
                    {[
                      { name: "Farm Overview", path: "/ml/farm-overview" },
                      { name: "Camera Feed", path: "/ml/camera-feed" },
                      { name: "Mortality Update", path: "/ml/mortality" },
                    ].map((item) => (
                      <div
                        key={item.name}
                        onClick={() => navigate(item.path)}
                        className="px-4 py-2 text-left hover:bg-green-50 hover:text-green-700 cursor-pointer transition"
                      >
                        {item.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </div>

          {/* FOOTER PROFILE */}
          <div className="p-3 border-t relative">
            <div
              onClick={() => setProfileOpen(!profileOpen)}
              className="bg-white rounded-lg p-3 cursor-pointer flex justify-between items-center shadow"
            >
              <div>
                <p className="text-sm font-semibold">{name}</p>
                <p className="text-xs text-gray-500">{email}</p>
              </div>
              {profileOpen ? <ChevronUp /> : <ChevronDown />}
            </div>

            {profileOpen && (
              <div className="absolute bottom-20 left-3 right-3 bg-white border rounded-md shadow">
               

                <div
  onClick={() => {
    localStorage.clear();
    navigate("/admin-login");
  }}
  className="px-4 py-2 flex items-center gap-2 hover:bg-red-100 text-red-600 cursor-pointer"
>
  <LogOut size={16} />
  <span>Logout</span>
</div>

              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
